import React, { useMemo, useState } from 'react'
import { Table, Button, Badge, Alert, Input, FormGroup, ButtonGroup } from 'reactstrap'
import { useQuery } from '@apollo/client'
import { GET_MESSAGE_TEMPLATES } from './Queries'
import CreateEditMessageTemplateModal from './CreateEditMessageTemplateModal'

const formatTimestamp = (value) => {
    if (!value) return '—'
    try {
        return new Date(value).toLocaleString()
    } catch (error) {
        return value
    }
}

const snippet = (value, length = 120) => {
    if (!value) return ''
    if (value.length <= length) return value
    return `${value.slice(0, length)}…`
}

export default function MessageTemplateManagement() {
    const [showModal, setShowModal] = useState(false)
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [searchTerm, setSearchTerm] = useState('')
    const [showInactive, setShowInactive] = useState(false)
    const [feedback, setFeedback] = useState(null)

    const { data, loading, error, refetch } = useQuery(GET_MESSAGE_TEMPLATES, {
        variables: { first: 100 },
        fetchPolicy: 'network-only'
    })

    const templates = data?.allMessageTemplates?.edges?.map(edge => edge.node) || []

    const filteredTemplates = useMemo(() => {
        const base = showInactive ? templates : templates.filter(template => template.isActive)
        if (!searchTerm.trim()) {
            return base
        }
        const lower = searchTerm.trim().toLowerCase()
        return base.filter(template => {
            const haystack = [
                template.title || '',
                template.shortcut || '',
                template.content || ''
            ].join(' ').toLowerCase()
            return haystack.includes(lower)
        })
    }, [templates, showInactive, searchTerm])

    const handleCreate = () => {
        setSelectedTemplate(null)
        setShowModal(true)
    }

    const handleEdit = (template) => {
        setSelectedTemplate(template)
        setShowModal(true)
    }

    const closeModal = () => {
        setShowModal(false)
        setSelectedTemplate(null)
    }

    const handleModalSuccess = (message) => {
        refetch()
        setFeedback({
            type: 'success',
            message: message || 'Template saved successfully.'
        })
    }

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        )
    }

    if (error) {
        return <Alert color="danger">Error loading message templates: {error.message}</Alert>
    }

    const showEmptyState = filteredTemplates.length === 0

    return (
        <div className="comms-admin-section">
            <div className="comms-admin-section-header">
                <div>
                    <h3>Message Templates</h3>
                    <p className="text-muted">Create canned responses for your agents to use across conversations.</p>
                </div>
                <div>
                    <Button color="primary" onClick={handleCreate}>
                        <i className="fa fa-plus"></i> New Template
                    </Button>
                </div>
            </div>

            {feedback && (
                <Alert color={feedback.type} toggle={() => setFeedback(null)}>
                    {feedback.message}
                </Alert>
            )}

            <div className="comms-admin-filters mb-3">
                <FormGroup className="mb-0 flex-grow-1">
                    <Input
                        type="search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search templates by title, shortcut, or content…"
                    />
                </FormGroup>
            </div>

            {showEmptyState ? (
                <Alert color="info">
                    {templates.length === 0
                        ? 'No templates yet. Create your first canned response to help agents reply faster.'
                        : (showInactive ? 'No templates match your filters.' : 'No active templates match your search.')}
                </Alert>
            ) : (
                <Table responsive striped hover className="comms-admin-table">
                    <thead>
                        <tr>
                            <th>Title</th>
                            <th>Shortcut</th>
                            <th>Preview</th>
                            <th>Status</th>
                            <th>Last Updated</th>
                            <th style={{ width: '120px' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredTemplates.map((template) => (
                            <tr key={template.id}>
                                <td>
                                    <strong>{template.title}</strong>
                                </td>
                                <td>
                                    {template.shortcut ? (
                                        <code>{template.shortcut}</code>
                                    ) : (
                                        <span className="text-muted">—</span>
                                    )}
                                </td>
                                <td>{snippet(template.content)}</td>
                                <td>
                                    {template.isActive ? (
                                        <Badge color="success">Active</Badge>
                                    ) : (
                                        <Badge color="secondary">Inactive</Badge>
                                    )}
                                </td>
                                <td>{formatTimestamp(template.dateModified || template.dateAdded)}</td>
                                <td>
                                    <ButtonGroup size="sm">
                                        <Button color="info" outline onClick={() => handleEdit(template)}>
                                            <i className="fa fa-edit"></i> Edit
                                        </Button>
                                    </ButtonGroup>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            {showModal && (
                <CreateEditMessageTemplateModal
                    isOpen={showModal}
                    toggle={closeModal}
                    template={selectedTemplate}
                    onSuccess={handleModalSuccess}
                />
            )}
        </div>
    )
}
